<template>
  <div class="record-form">
    <div class="record-form__intro">
      <h2 class="record-form__title">Datos cliente</h2>
      <p class="record-form__copy">Registra la ficha principal con datos limpios y consistentes para mantener el directorio ordenado.</p>
    </div>

    <p v-if="isReadOnly" class="record-form__notice">
      El cliente está inactivo. La edición está bloqueada, excepto el estado.
    </p>

    <form class="record-form__grid" @submit.prevent="save">
      <div class="record-form__field record-form__field--full">
        <label class="record-form__label" for="company_name">Razon Social</label>
        <input
          v-model.trim="company.name"
          class="record-form__input"
          type="text"
          id="company_name"
          placeholder="Nombre o razon social"
          :disabled="isReadOnly"
        >
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="company_rut">RUT</label>
        <input
          v-model.trim="fullRut"
          class="record-form__input"
          type="text"
          id="company_rut"
          placeholder="12.345.678-9"
          :disabled="isReadOnly"
        >
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="status">Estado</label>
        <div class="record-form__select-wrap">
          <select v-model="company.status" id="status" class="record-form__select">
            <option v-for="status in companyStatuses" :key="status" :value="status">{{ status }}</option>
          </select>
        </div>
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="client_type">Tipo de cliente</label>
        <div class="record-form__select-wrap">
          <select v-model="company.client_type" id="client_type" class="record-form__select" :disabled="isReadOnly">
            <option v-for="clientType in clientTypes" :key="clientType.value" :value="clientType.value">{{ clientType.label }}</option>
          </select>
        </div>
      </div>

      <div v-if="isNaturalPerson" class="record-form__field record-form__field--full record-form__toggle-field">
        <label class="record-form__toggle">
          <input
            v-model="record.requires_legal_fiscal_info"
            class="record-form__toggle-input"
            type="checkbox"
            :disabled="isReadOnly"
          >
          <span>
            <strong>Requiere información legal y fiscal</strong>
            <small>Activa este bloque cuando la Persona Natural deba completar datos tributarios para el cálculo de ficha completa.</small>
          </span>
        </label>
      </div>

      <p v-if="isNaturalPerson && !record.requires_legal_fiscal_info" class="record-form__hint record-form__field--full">
        Los campos tributarios y legales no serán considerados en el cálculo de ficha completa mientras esta opción esté desactivada.
      </p>

      <div class="record-form__field">
        <label class="record-form__label" for="id_remu">Código REMU</label>
        <input
          v-model="record.id_remu"
          class="record-form__input"
          type="text"
          id="id_remu"
          placeholder="Código REMU"
          :disabled="isLegalFiscalFieldDisabled"
        >
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="id_conta">Código Conta</label>
        <input
          v-model="record.id_conta"
          class="record-form__input"
          type="text"
          id="id_conta"
          placeholder="Código Conta"
          :disabled="isLegalFiscalFieldDisabled"
        >
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="start_activities">Inicio Actividad</label>
        <input
          v-model="record.start_activities"
          class="record-form__input"
          type="date"
          id="start_activities"
          :disabled="isLegalFiscalFieldDisabled"
        >
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="organizational_type">Tipo Organizacional</label>
        <input
          v-model="record.organizational_type"
          class="record-form__input"
          type="text"
          id="organizational_type"
          placeholder="Tipo Organizacional"
          :disabled="isLegalFiscalFieldDisabled"
        >
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="taxRegime">Régimen Tributario</label>
        <div class="record-form__select-wrap">
          <select v-model="record.tax_regime_id" id="taxRegime" class="record-form__select" :disabled="isLegalFiscalFieldDisabled">
            <option v-for="taxRegime in taxRegimes" :key="taxRegime.id" :value="taxRegime.id">{{ taxRegime.name }}</option>
          </select>
        </div>
      </div>

      <div class="record-form__field">
        <label class="record-form__label" for="billyType">Tipo Facturador</label>
        <div class="record-form__select-wrap">
          <select v-model="record.biller_type_id" id="billyType" class="record-form__select" :disabled="isLegalFiscalFieldDisabled">
            <option v-for="billerType in billerTypes" :key="billerType.id" :value="billerType.id">{{ billerType.name }}</option>
          </select>
        </div>
      </div>

      <p v-if="error" class="record-form__error">{{ strError }}</p>

      <ReviewSelector v-model="reviewerId" :company-id="company.id" />
      <div class="record-form__actions">
        <button class="button record-form__action record-form__action--primary" type="submit">Grabar</button>
      </div>
    </form>
  </div>
</template>

<script>
import ReviewSelector from './ReviewSelector.vue'
import { normalizeDateInputValue } from '@/utils/date'
import { formatRut, isValidRut, splitRut } from '@/utils/rut'

export default {
  components: { ReviewSelector },
  data: () => ({
    reviewerId: null,
    record: {
      id_remu: '',
      id_conta: '',
      tax_regime_id: -1,
      biller_type_id: -1,
      organizational_type: '',
      requires_legal_fiscal_info: true
    },
    companyStatuses: ['Activo', 'Inactivo', 'Esporádico', 'TTG (Trámite de término de Giro)'],
    clientTypes: [
      { value: 'empresa', label: 'Empresa' },
      { value: 'persona_natural', label: 'Persona Natural' }
    ],
    addresses: [],
    partners: [],
    error: false,
    strError: '',
    tmpAddress: { name: '', address: '' },
    tmpPartner: { rut: '', name: '', participation_perc: 100, birthday: '' },
    persistedStatus: 'Activo'
  }),
  props: {
    editRecord: Object,
    taxRegimes: Array,
    billerTypes: Array,
    company: Object,
    recordProp: Object,
    redirectOnSave: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    isReadOnly () {
      return this.persistedStatus === 'Inactivo'
    },
    isNaturalPerson () {
      return this.company && this.company.client_type === 'persona_natural'
    },
    isLegalFiscalFieldDisabled () {
      return this.isReadOnly || (this.isNaturalPerson && !this.record.requires_legal_fiscal_info)
    },
    fullRut: {
      get: function () {
        return formatRut(this.company.rut, this.company.dv)
      },
      set: function (newValue) {
        const { rut, dv } = splitRut(newValue)
        this.company.rut = rut
        this.company.dv = dv
      }
    }
  },
  methods: {
    save () {
      this.error = false
      this.strError = ''
      const hasRutData = Boolean(this.company.rut || this.company.dv)
      if (hasRutData && !isValidRut(`${this.company.rut}-${this.company.dv}`)) {
        this.error = true
        this.strError = 'El RUT ingresado no es válido.'
        return
      }

      if (!this.isNaturalPerson) {
        this.record.requires_legal_fiscal_info = true
      }

      this.record.company_id = this.company.id
      this.$axios
        .post('company/saveFicha', { company: this.company, record: this.record, reviewer_id: this.reviewerId })
        .then(() => {
          window.dispatchEvent(new Event('company-tasks-updated'))
          if (this.redirectOnSave) {
            this.$router.push({ name: 'CompanyShow', params: { id: this.company.id } })
          } else {
            this.$emit('saved')
          }
        })
        .catch(error => {
          const message = error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'No fue posible guardar la ficha del cliente.'
          this.error = true
          this.strError = message
          console.log(error)
        })
    }
  },
  watch: {
    recordProp: {
      immediate: true,
      handler (newVal) {
        this.record = {
          ...(newVal || {})
        }
        if (typeof this.record.requires_legal_fiscal_info !== 'boolean') {
          this.record.requires_legal_fiscal_info = true
        }
        if (this.record.start_activities) {
          this.record.start_activities = normalizeDateInputValue(this.record.start_activities)
        }
      }
    },
    company: {
      immediate: true,
      deep: true,
      handler (newVal) {
        this.persistedStatus = newVal && newVal.status ? newVal.status : 'Activo'
        if (!newVal || newVal.client_type !== 'persona_natural') {
          this.record.requires_legal_fiscal_info = true
        }
      }
    }
  }
}
</script>

<style scoped>
.record-form {
  color: #201a14;
}

.record-form__intro {
  margin-bottom: 1.35rem;
}

.record-form__title {
  margin: 0;
  font-size: clamp(1.8rem, 2.8vw, 2.25rem);
  font-weight: 650;
  letter-spacing: -0.04em;
  line-height: 1.02;
}

.record-form__copy {
  max-width: 44rem;
  margin: 0.85rem 0 0;
  color: rgba(68, 55, 41, 0.68);
  font-size: 1rem;
  line-height: 1.65;
}

.record-form__notice {
  margin: 0 0 1.15rem;
  padding: 0.95rem 1rem;
  border: 1px solid rgba(120, 110, 102, 0.18);
  border-radius: 18px;
  background: rgba(244, 240, 236, 0.9);
  color: #655b55;
}

.record-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1rem;
}

.record-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.record-form__field--full,
.record-form__actions,
.record-form__error {
  grid-column: 1 / -1;
}

.record-form__toggle-field {
  margin-top: 0.2rem;
}

.record-form__label {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(68, 55, 41, 0.56);
}

.record-form__input,
.record-form__select {
  width: 100%;
  min-height: 3.4rem;
  padding: 0 1rem;
  border: 1px solid rgba(89, 72, 55, 0.1);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
  color: #261f19;
  font-size: 1rem;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.94),
    0 10px 22px rgba(69, 49, 30, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.record-form__input:focus,
.record-form__select:focus {
  outline: none;
  border-color: rgba(83, 126, 165, 0.3);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.94),
    0 0 0 4px rgba(111, 188, 230, 0.12);
}

.record-form__input:disabled,
.record-form__select:disabled {
  cursor: not-allowed;
  color: rgba(68, 55, 41, 0.45);
  background: rgba(244, 240, 236, 0.72);
  box-shadow: none;
}

.record-form__toggle {
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
  padding: 1rem 1.05rem;
  border: 1px solid rgba(89, 72, 55, 0.1);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.72);
}

.record-form__toggle-input {
  margin-top: 0.2rem;
  width: 1rem;
  height: 1rem;
  accent-color: #6d7d4d;
}

.record-form__toggle strong,
.record-form__toggle small {
  display: block;
}

.record-form__toggle strong {
  color: #261f19;
  font-size: 0.96rem;
}

.record-form__toggle small,
.record-form__hint {
  color: rgba(68, 55, 41, 0.68);
  line-height: 1.5;
}

.record-form__toggle small {
  margin-top: 0.3rem;
  font-size: 0.85rem;
}

.record-form__hint {
  margin: -0.1rem 0 0;
  font-size: 0.88rem;
}

.record-form__select-wrap {
  position: relative;
}

.record-form__select {
  appearance: none;
  cursor: pointer;
  padding-right: 2.75rem;
  background-image: linear-gradient(45deg, transparent 50%, #67c2ed 50%), linear-gradient(135deg, #67c2ed 50%, transparent 50%);
  background-position: calc(100% - 22px) calc(50% - 3px), calc(100% - 16px) calc(50% - 3px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

.record-form__error {
  margin: 0;
  color: #a44949;
}

.record-form__actions {
  display: flex;
  justify-content: flex-start;
  padding-top: 0.25rem;
}

.record-form__action {
  min-height: 3rem;
  padding: 0 1.15rem;
  border-radius: 999px;
  font-weight: 600;
  box-shadow: none;
}

.record-form__action--primary {
  border: 0;
  background: linear-gradient(135deg, #201a14, #4c3b2b);
  color: #fff7ef;
  box-shadow: 0 14px 30px rgba(42, 30, 18, 0.18);
}

.record-form__action--primary:hover,
.record-form__action--primary:focus {
  color: #fff7ef;
}

@media (max-width: 640px) {
  .record-form__grid {
    grid-template-columns: 1fr;
  }

  .record-form__title {
    font-size: 1.7rem;
  }

  .record-form__copy {
    font-size: 0.95rem;
  }

  .record-form__actions {
    justify-content: stretch;
  }

  .record-form__action {
    width: 100%;
  }
}
</style>


